// Supabase/PostgreSQL 에러 코드를 번역 키로 매핑
const ERROR_CODE_TO_KEY: Record<string, string> = {
  // PostgreSQL 에러 코드
  "23505": "duplicateData",
  "23503": "referenceNotFound",
  "23502": "requiredFieldMissing",
  "42703": "databaseError",
  "42P01": "databaseError",
  PGRST116: "dataNotFound",
  // 일반 에러
  NETWORK_ERROR: "networkError",
  TIMEOUT: "timeout",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
};

// 정규식 패턴으로 동적 값이 포함된 에러 메시지 매핑
const AUTH_ERROR_PATTERNS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /Email address ".*" is invalid/i, key: "auth.invalidEmail" },
  { pattern: /Invalid email/i, key: "auth.invalidEmail" },
  { pattern: /email.*invalid/i, key: "auth.invalidEmail" },
];

// Supabase Auth 에러 메시지를 번역 키로 매핑
const AUTH_ERROR_TO_KEY: Record<string, string> = {
  // OAuth 인앱 브라우저 차단 (Google disallowed_useragent 등)
  disallowed_useragent: "auth.inAppBrowserBlocked",
  access_denied: "auth.inAppBrowserBlocked",
  auth_callback_inapp: "auth.inAppBrowserBlocked",
  // 로그인 에러
  "Invalid login credentials": "auth.invalidCredentials",
  "Invalid credentials": "auth.invalidCredentials",
  "Email not confirmed": "auth.emailNotConfirmed",
  "Invalid email or password": "auth.invalidCredentials",
  // 회원가입 에러
  "User already registered": "auth.userAlreadyRegistered",
  "Email already registered": "auth.userAlreadyRegistered",
  "Password should be at least 6 characters": "auth.passwordTooShort",
  "Signup requires a valid password": "auth.invalidPassword",
  "Unable to validate email address: invalid format": "auth.invalidEmail",
  // 비밀번호 관련
  "New password should be different from the old password": "auth.samePassword",
  "Password is too weak": "auth.weakPassword",
  // Rate limit
  "Email rate limit exceeded": "auth.emailRateLimitExceeded",
  "For security purposes, you can only request this once every 60 seconds":
    "auth.rateLimitExceeded",
  "Too many requests": "auth.rateLimitExceeded",
  // 세션/토큰 에러
  "Token has expired or is invalid": "auth.tokenExpired",
  "Invalid Refresh Token": "auth.sessionExpired",
  "Refresh Token Not Found": "auth.sessionExpired",
  "Session not found": "auth.sessionNotFound",
  // 기타
  "User not found": "auth.userNotFound",
  "Email link is invalid or has expired": "auth.linkExpired",
  // 커스텀 에러 (Server Action에서 반환)
  INVALID_CREDENTIALS: "auth.invalidCredentials",
  EMAIL_NOT_CONFIRMED: "auth.emailNotConfirmed",
  PARTNER_REDIRECT: "auth.partnerRedirect",
  LOGIN_ERROR: "auth.loginError",
  SIGNUP_SUCCESS: "auth.signupSuccess",
  SIGNUP_ERROR: "auth.signupError",
  ACCOUNT_BANNED: "auth.accountBanned",
  ACCOUNT_WITHDRAWN: "auth.accountWithdrawn",
};

// 에러 메시지에서 에러 코드 추출
function extractErrorCode(message: string): string | null {
  // PostgreSQL 에러 코드 패턴 (예: "42703", "23505")
  const pgMatch = message.match(/\b([0-9]{5}|[0-9]{2}[A-Z][0-9]{2})\b/);
  if (pgMatch) return pgMatch[1];

  // PostgREST 에러 코드 패턴 (예: "PGRST116")
  const pgrstMatch = message.match(/PGRST\d+/);
  if (pgrstMatch) return pgrstMatch[0];

  return null;
}

// 에러에서 번역 키 추출 (컴포넌트에서 t 함수와 함께 사용)
export function getErrorKey(error: unknown): string {
  let message = "";
  let code: string | undefined;

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object") {
    // Supabase 에러 객체 처리
    const errorObj = error as {
      message?: string;
      code?: string;
      details?: string;
    };
    if (errorObj.message) message = errorObj.message;
    if (errorObj.code) code = errorObj.code;
  }

  // Supabase Auth 에러 메시지 확인 (정확히 일치)
  if (message && AUTH_ERROR_TO_KEY[message]) {
    return AUTH_ERROR_TO_KEY[message];
  }

  // Auth 에러 부분 일치 확인
  for (const [authError, key] of Object.entries(AUTH_ERROR_TO_KEY)) {
    if (message.includes(authError)) {
      return key;
    }
  }

  // 에러 코드가 있으면 먼저 확인
  if (code && ERROR_CODE_TO_KEY[code]) {
    return ERROR_CODE_TO_KEY[code];
  }

  // 메시지에서 에러 코드 추출 후 변환
  const errorCode = extractErrorCode(message);
  if (errorCode && ERROR_CODE_TO_KEY[errorCode]) {
    return ERROR_CODE_TO_KEY[errorCode];
  }

  // 기술적인 에러 메시지 패턴 확인
  if (message.includes("column") && message.includes("does not exist")) {
    return "databaseError";
  }
  if (message.includes("foreign key constraint")) {
    return "linkedDataExists";
  }
  if (
    message.includes("unique constraint") ||
    message.includes("duplicate key")
  ) {
    return "duplicateData";
  }
  if (message.includes("Failed to fetch") || message.includes("NetworkError")) {
    return "networkError";
  }
  if (message.includes("timeout") || message.includes("Timeout")) {
    return "timeout";
  }

  // 기본값
  return "genericError";
}

// Auth 에러 메시지를 번역 키로 변환 (Server Action에서 사용)
export function getAuthErrorKey(message: string): string {
  // 1. 정확히 일치
  if (AUTH_ERROR_TO_KEY[message]) {
    return AUTH_ERROR_TO_KEY[message];
  }

  // 2. 부분 일치
  for (const [authError, key] of Object.entries(AUTH_ERROR_TO_KEY)) {
    if (message.includes(authError)) {
      return key;
    }
  }

  // 3. 정규식 패턴 매칭 (동적 값이 포함된 에러 메시지 처리)
  for (const { pattern, key } of AUTH_ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return key;
    }
  }

  // 기본값 - 원본 메시지 반환을 위해 빈 문자열
  return "";
}

// 에러 메시지 반환 (번역 함수 t를 전달받아 사용)
export function getErrorMessage(
  error: unknown,
  t?: (key: string) => string
): string {
  const errorKey = getErrorKey(error);

  // 번역 함수가 제공되면 사용
  if (t) {
    return t(`errors.${errorKey}`);
  }

  // 번역 함수가 없으면 원본 메시지 또는 기본 메시지 반환
  let message = "";

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "string") {
    message = error;
  } else if (error && typeof error === "object") {
    const errorObj = error as { message?: string };
    if (errorObj.message) message = errorObj.message;
  }

  // 한국어 메시지는 그대로 반환
  if (message && /[가-힣]/.test(message)) {
    return message;
  }

  // 영문 기술적 메시지는 일반 에러로 변환 (fallback)
  if (!message || message.length > 100 || message.includes("Error:")) {
    return "오류가 발생했습니다. 다시 시도해주세요.";
  }

  return message;
}
